import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { DatabaseIcon, BarChart3Icon, TrendingUpIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { KPICard } from '../components/KPICard';
import { CardSkeleton, ChartSkeleton } from '../components/LoadingSkeleton';
const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#22c55e', '#3b82f6'];
export function CapacityAnalysis() {
  const {
    data,
    loading,
    error
  } = useSupabaseQuery({
    limit: 5000
  });
  const {
    kpis,
    chartData,
    capacities
  } = useMemo(() => {
    if (!data.length) {
      return {
        kpis: null,
        chartData: [],
        capacities: []
      };
    }
    const totalSites = data.length;
    const capacityCount = new Map<string, number>();
    const programCount = new Map<string, number>();
    const programMap = new Map<string, Map<string, number>>();
    const capacitySet = new Set<string>();
    data.forEach(item => {
      const program = item.Program || 'Unknown';
      const capGroup = item.GroupingKapasitas || 'Unknown';
      capacitySet.add(capGroup);
      capacityCount.set(capGroup, (capacityCount.get(capGroup) || 0) + 1);
      programCount.set(program, (programCount.get(program) || 0) + 1);
      if (!programMap.has(program)) {
        programMap.set(program, new Map());
      }
      const capMap = programMap.get(program)!;
      capMap.set(capGroup, (capMap.get(capGroup) || 0) + 1);
    });
    const dominantCapacity = Array.from(capacityCount.entries()).sort((a, b) => b[1] - a[1])[0];
    const topProgram = Array.from(programCount.entries()).sort((a, b) => b[1] - a[1])[0];
    const capacities = Array.from(capacitySet).sort((a, b) => Number(a) - Number(b)).slice(0, 6); // 🔥 biar legend gak kepanjangan

    const chartData = Array.from(programMap, ([program, capMap]) => {
      const obj: any = {
        name: program
      };
      capacities.forEach(cap => {
        obj[cap] = capMap.get(cap) || 0;
      });
      return obj;
    });
    return {
      kpis: {
        totalSites,
        dominantCapacity,
        topProgram
      },
      chartData,
      capacities
    };
  }, [data]);
  if (error) {
    return <div className="p-6 text-red-400 bg-red-950/30 rounded-xl border border-red-900" data-id="element-618" data-mp="296u9qehc7hrp">
        Error: {error}
      </div>;
  }
  return <div className="space-y-6" data-id="element-619" data-mp="24f2omjgya48y">
      <h2 className="text-xl font-bold text-slate-100 tracking-tight" data-id="element-620" data-mp="24e12njyebxcg">
        Capacity Analysis
      </h2>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-621" data-mp="1ui6gieuyrk4x">
        {loading || !kpis ? <>
            <CardSkeleton data-id="element-622" data-mp="1pjd12gw49j24" />
            <CardSkeleton data-id="element-623" data-mp="1phjsux5uuqs0" />
            <CardSkeleton data-id="element-624" data-mp="1pi21kt9pexgt" />
          </> : <>
            <KPICard title="Total Sites" value={kpis.totalSites.toLocaleString()} icon={DatabaseIcon} data-id="element-625" data-mp="1lib3e4hulf29" />

            <KPICard title="Dominant Capacity" value={kpis.dominantCapacity ? `${kpis.dominantCapacity[0]} (${kpis.dominantCapacity[1]})` : '-'} icon={BarChart3Icon} data-id="element-626" data-mp="50su97d1txxw" />

            <KPICard title="Top Program" value={kpis.topProgram ? `${kpis.topProgram[0]} (${kpis.topProgram[1]})` : '-'} icon={TrendingUpIcon} data-id="element-627" data-mp="3dgm8tgntkzcb" />
          </>}
      </div>

      {/* CHART */}
      <div className="grid grid-cols-1 gap-6" data-id="element-628" data-mp="3s60jqt71nott">
        {loading ? <ChartSkeleton data-id="element-629" data-mp="3s7bnwu4eiylg" /> : <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="glass-panel p-6 rounded-xl h-[460px] flex flex-col" data-id="element-630" data-mp="2uqweeunyyfkx">
            <h3 className="text-sm font-semibold text-slate-100 mb-4 uppercase tracking-wider" data-id="element-631" data-mp="1587odnclzlms">
              Capacity Distribution per Program
            </h3>

            {/* 🔥 FIX AREA */}
            <div className="flex-1 min-h-0" data-id="element-632" data-mp="1ezbxw7jimea8">
              <ResponsiveContainer width="100%" height="100%" data-id="element-633" data-mp="1f03mgyep3br0">
                <BarChart data={chartData} data-id="element-634" data-mp="1a7129gvt5enh">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" data-id="element-635" data-mp="1a5zeqcxluzov" />

                  <XAxis dataKey="name" stroke="#cbd5f5" fontSize={12} tickLine={false} axisLine={false} data-id="element-636" data-mp="1ovf0tfm6ygy8" />

                  <YAxis stroke="#cbd5f5" fontSize={12} tickLine={false} axisLine={false} data-id="element-637" data-mp="1xvaim3a95a78" />

                  <Tooltip contentStyle={{
                backgroundColor: '#020617',
                border: '1px solid #334155',
                borderRadius: '10px',
                color: '#e2e8f0'
              }} labelStyle={{
                color: '#f1f5f9'
              }} data-id="element-638" data-mp="3lq2i4n2bgg9" />

                  {/* 🔥 LEGEND FIX TOTAL */}
                  <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{
                maxHeight: '60px',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '11px',
                color: '#cbd5f5',
                paddingTop: '10px'
              }} data-id="element-639" data-mp="21fc1zwqr30ik" />

                  {capacities.map((cap, i) => <Bar key={cap} dataKey={cap} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} data-id="element-640" data-mp="3gw2ol61u2dlv" data-mpi={i} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>}
      </div>
    </div>;
}